import { useEffect, useMemo, useState } from 'react';
import type { CandidatesDto, CreateDrawingRequest, DrawingDto, DrawingStartedDto, PeriodName } from '@shared/types';
import { ApiError } from '@shared/api';
import { Wheel } from '@shared/Wheel';
import { dateOnly, money, titleCase } from '@shared/format';
import { prizeDisplayName } from '@shared/prize';
import { errorMessage, useApp } from '../context';
import { navigate } from '../router';
import { Card, ErrorBox, Loading, Modal, Pill, Stepper, TopBar } from '../components/ui';

const PERIODS: PeriodName[] = ['weekly', 'monthly', 'quarterly', 'annual'];
const SPIN_OPTIONS = [5, 8, 10, 15, 20, 30];
// The value is admin-only recordkeeping. Keep dollar amounts out of the name: the name is what the shop TV shows.
const PRIZE_PRESETS = [
  { key: 'gc25', label: 'Gift Card ($25)', name: 'Gift Card', value: 25 },
  { key: 'gc50', label: 'Gift Card ($50)', name: 'Gift Card', value: 50 },
  { key: 'gc100', label: 'Gift Card ($100)', name: 'Gift Card', value: 100 },
];
const presetFor = (name: string, value: string) => PRIZE_PRESETS.find((p) => p.name === name && Number(value) === p.value);

interface Settings {
  prizeName: string; prizeValue: string; spinSeconds: number; celebrationSeconds: number; notes: string; scheduledLocal: string;
}

function toLocalInput(utc: string | null): string {
  if (!utc) return '';
  const d = new Date(utc);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function DrawingWizard({ drawingType, drawingId }: { drawingType?: string; drawingId?: string }) {
  const { api, online, toast } = useApp();
  const [drawing, setDrawing] = useState<DrawingDto | null>(null);
  const [type, setType] = useState<PeriodName>(PERIODS.includes(drawingType as PeriodName) ? (drawingType as PeriodName) : 'weekly');
  const [reference, setReference] = useState<'current' | 'previous'>('current');
  const [threshold, setThreshold] = useState<number | null>(null);
  // Text draft so typing "10" never renders as "010"; committed (debounced) to `threshold`, which drives the server query.
  const [thresholdDraft, setThresholdDraft] = useState<string | null>(null);
  useEffect(() => {
    if (thresholdDraft === null || thresholdDraft === '') return;
    const n = Math.max(0, Math.floor(Number(thresholdDraft)));
    if (!Number.isFinite(n)) return;
    const t = setTimeout(() => setThreshold(n), 350);
    return () => clearTimeout(t);
  }, [thresholdDraft]);
  const [cands, setCands] = useState<CandidatesDto | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Optional admin pick. Admin-only: the shop TV spins normally and lands on this person.
  const [chosen, setChosen] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'eligible'>('all');
  const [dept, setDept] = useState('');
  const [settings, setSettings] = useState<Settings>({ prizeName: 'Gift Card', prizeValue: '100', spinSeconds: 10, celebrationSeconds: 30, notes: '', scheduledLocal: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | 'start' | 'offline' | 'cancel'>(null);
  const [initialised, setInitialised] = useState(!drawingId);

  // Resume an existing drawing.
  useEffect(() => {
    if (!drawingId) return;
    api.get<DrawingDto>(`/api/drawings/${drawingId}`).then((d) => {
      if (d.status === 'Started' || d.status === 'Completed') { navigate(`/drawings/${d.drawingId}/live`); return; }
      setDrawing(d);
      setType(d.drawingType.toLowerCase() as PeriodName);
      setReference(d.periodReference.toLowerCase() === 'previous' ? 'previous' : 'current');
      setThreshold(d.threshold);
      setSelected(new Set(d.entries.map((e) => e.employeeId)));
      setChosen(d.chosenWinnerEmployeeId ?? null);
      setSettings({
        prizeName: d.prizeName, prizeValue: String(d.prizeValue), spinSeconds: d.spinSeconds, celebrationSeconds: d.celebrationSeconds,
        notes: d.notes ?? '', scheduledLocal: toLocalInput(d.scheduledForUtc),
      });
      setStep(d.status === 'Staged' || d.status === 'Cancelled' || d.entries.length >= 2 ? 3 : 1);
      setInitialised(true);
    }).catch((e) => setLoadError(errorMessage(e)));
  }, [api, drawingId]);

  // Candidate list (recomputed from the ledger by the server for the selected period + threshold).
  useEffect(() => {
    if (!initialised) return;
    const q = new URLSearchParams({ type, reference });
    if (threshold !== null) q.set('threshold', String(threshold));
    let live = true;
    api.get<CandidatesDto>(`/api/drawings/candidates?${q}`).then((c) => {
      if (!live) return;
      setCands(c);
      if (threshold === null) setThreshold(c.threshold);
      // New drawing: preselect everyone who met the threshold. Admin makes the final selection.
      if (!drawing) setSelected(new Set(c.candidates.filter((x) => x.eligible).map((x) => x.employeeId)));
    }).catch((e) => live && setLoadError(errorMessage(e)));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, type, reference, threshold, initialised]);

  const locked = !!drawing && drawing.status !== 'Draft';
  const departments = useMemo(() => [...new Set((cands?.candidates ?? []).map((c) => c.department).filter(Boolean) as string[])].sort(), [cands]);
  const visible = useMemo(() => (cands?.candidates ?? []).filter((c) =>
    (filter === 'all' || c.eligible) && (!dept || c.department === dept) &&
    (!search || c.displayName.toLowerCase().includes(search.toLowerCase()))), [cands, filter, dept, search]);

  const toggle = (id: string) => {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    if (selected.has(id) && chosen === id) setChosen(null);
  };
  const toggleChosen = (id: string) => {
    if (chosen === id) { setChosen(null); return; }
    setChosen(id);
    setSelected((s) => (s.has(id) ? s : new Set([...s, id])));
  };
  // A pick must be on the wheel.
  useEffect(() => { if (chosen && !selected.has(chosen)) setChosen(null); }, [selected, chosen]);
  const chosenName = chosen ? (cands?.candidates.find((c) => c.employeeId === chosen)?.displayName ?? drawing?.entries.find((e) => e.employeeId === chosen)?.displayName ?? null) : null;
  const chosenWonLast = !!chosen && cands?.previousWinner?.employeeId === chosen;
  const selectAllVisible = () => setSelected((s) => new Set([...s, ...visible.map((v) => v.employeeId)]));
  const clearAll = () => { setSelected(new Set()); setChosen(null); };

  const prizeValueNum = Number(settings.prizeValue);
  const settingsValid = settings.prizeName.trim().length > 0 && settings.prizeValue.trim() !== '' && Number.isFinite(prizeValueNum) && prizeValueNum >= 0
    && /^\d+(\.\d{1,2})?$/.test(settings.prizeValue.trim());

  const orderedEntrants = useMemo(() => {
    if (drawing && drawing.status !== 'Draft') return [...drawing.entries].sort((a, b) => a.displayOrder - b.displayOrder).map((e) => e.displayName);
    const byId = new Map((cands?.candidates ?? []).map((c) => [c.employeeId, c.displayName]));
    return [...selected].map((id) => byId.get(id) ?? drawing?.entries.find((e) => e.employeeId === id)?.displayName ?? '—');
  }, [drawing, cands, selected]);

  async function saveDraft(): Promise<DrawingDto> {
    const scheduledForUtc = settings.scheduledLocal ? new Date(settings.scheduledLocal).toISOString() : null;
    let d = drawing;
    if (!d) {
      const req: CreateDrawingRequest = {
        drawingType: type, periodReference: reference, prizeName: settings.prizeName.trim(), prizeValue: prizeValueNum,
        spinSeconds: settings.spinSeconds, celebrationSeconds: settings.celebrationSeconds, notes: settings.notes.trim() || null,
        scheduledForUtc, threshold,
      };
      d = await api.post<DrawingDto>('/api/drawings', req);
    } else {
      d = await api.put<DrawingDto>(`/api/drawings/${d.drawingId}`, {
        prizeName: settings.prizeName.trim(), prizeValue: prizeValueNum, spinSeconds: settings.spinSeconds,
        celebrationSeconds: settings.celebrationSeconds, notes: settings.notes.trim() || null, scheduledForUtc,
      });
    }
    d = await api.post<DrawingDto>(`/api/drawings/${d.drawingId}/entries`, { employeeIds: [...selected], chosenWinnerEmployeeId: chosen && selected.has(chosen) ? chosen : null });
    setDrawing(d);
    if (!drawingId) history.replaceState(null, '', `#/drawings/${d.drawingId}`);
    return d;
  }

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true); setError(null);
    try { return await fn(); } catch (e) { setError(errorMessage(e)); return undefined; } finally { setBusy(false); }
  }

  const goPreview = () => run(async () => { await saveDraft(); setStep(3); });
  const stage = () => run(async () => {
    const d = drawing?.status === 'Draft' ? await saveDraft() : drawing!;
    const staged = await api.post<DrawingDto>(`/api/drawings/${d.drawingId}/stage`);
    setDrawing(staged);
    toast('Drawing sent to the shop display');
  });
  const start = (confirmOfflineRisk: boolean) => run(async () => {
    if (!navigator.onLine) throw new Error('You are offline. A drawing can only start with a confirmed server connection.');
    try {
      const res = await api.post<DrawingStartedDto>(`/api/drawings/${drawing!.drawingId}/start`, { confirmOfflineRisk });
      setConfirm(null);
      navigate(`/drawings/${res.drawing.drawingId}/live`);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'display_offline') { setConfirm('offline'); return; }
      setConfirm(null);
      throw e;
    }
  });
  const cancel = () => run(async () => {
    const d = await api.post<DrawingDto>(`/api/drawings/${drawing!.drawingId}/cancel`, { reason: 'Cancelled from admin PWA before start' });
    setDrawing(d); setConfirm(null); toast('Drawing cancelled'); navigate('/');
  });

  if (loadError) return <div className="page"><TopBar title="Drawing Setup" back="/" /><ErrorBox message={loadError} /></div>;
  if (!initialised || !cands) return <div className="page"><TopBar title="Drawing Setup" back="/" /><Loading /></div>;

  const title = `${titleCase(type)} Drawing Setup`;
  const back = step === 1 ? '/' : () => setStep((s) => (s === 3 && locked ? 3 : ((s - 1) as 1 | 2)));

  return (
    <div className="page wizard">
      <TopBar title={title} back={locked ? '/' : back} />
      <Stepper step={step} />
      {error && <ErrorBox message={error} />}

      {step === 1 && (
        <>
          <Card className="period-card">
            {!drawing && (
              <div className="seg-row">
                {PERIODS.map((p) => (
                  <button key={p} className={`seg ${p === type ? 'on' : ''}`} onClick={() => { setType(p); setThreshold(null); }}>{titleCase(p)}</button>
                ))}
              </div>
            )}
            <div className="row-between wrap">
              <div>
                <div className="card-kicker">Period</div>
                <div><strong>{cands.periodLabel}</strong> <span className="muted small">({dateOnly(cands.periodStart)} – {dateOnly(cands.periodEnd)})</span></div>
              </div>
              {!drawing && (
                <select className="input input-sm" value={reference} onChange={(e) => { setReference(e.target.value as 'current' | 'previous'); }} aria-label="Period">
                  <option value="current">Current {type.replace('ly', '').replace('annual', 'year')}</option>
                  <option value="previous">Previous {type.replace('ly', '').replace('annual', 'year')}</option>
                </select>
              )}
            </div>
            <label className="field-inline">
              <span>Qualifying threshold (points)</span>
              <input className="input input-sm tabular" type="text" inputMode="numeric" pattern="[0-9]*" aria-label="Qualifying threshold (points)" value={thresholdDraft ?? String(threshold ?? cands.threshold)}
                disabled={!!drawing} onChange={(e) => setThresholdDraft(e.target.value.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, ''))}
                onBlur={() => { if (thresholdDraft === '') setThresholdDraft(String(threshold ?? cands.threshold)); }} />
            </label>
            <div className="muted small">{cands.eligibleCount} of {cands.candidates.length} employees are eligible.</div>
            {cands.previousWinner && (
              <div className="req-note">No back-to-back winners: <strong>{cands.previousWinner.displayName}</strong> won the last drawing ({cands.previousWinner.reference}). Their name still shows on the wheel, but they can't be picked. Only admins see this.</div>
            )}
            {(cands.requiredForms?.length ?? 0) > 0 && (
              <div className="req-note">Required to be eligible: <strong>{cands.requiredForms!.join(', ')}</strong>. Anyone who hasn't submitted it this period doesn't qualify, even with enough points.</div>
            )}
          </Card>

          <div className="search-wrap">
            <input className="input" type="search" placeholder="Search employees…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search employees" />
          </div>
          <div className="filter-row">
            <button className={`chip ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>All</button>
            <button className={`chip ${filter === 'eligible' ? 'on' : ''}`} onClick={() => setFilter('eligible')}>Eligible only</button>
            <select className="input input-sm" value={dept} onChange={(e) => setDept(e.target.value)} aria-label="Department">
              <option value="">Department</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <Card className="table-card">
            <div className="emp-head"><span /><span>Employee</span><span className="right">Points</span><span className="center">Winner</span></div>
            <div className="emp-list">
              {visible.map((c) => (
                <label key={c.employeeId} className={`emp-row ${selected.has(c.employeeId) ? 'sel' : ''} ${chosen === c.employeeId ? 'picked' : ''}`}>
                  <input type="checkbox" checked={selected.has(c.employeeId)} onChange={() => toggle(c.employeeId)} />
                  <span>
                    {c.displayName}
                    {chosen === c.employeeId && <span className="pick-tag"> picked winner</span>}
                    {c.wonLastDrawing && <span className="below"> won last drawing: on the wheel, can't win{chosen === c.employeeId ? ' (your pick overrides this)' : ''}</span>}
                    {!c.eligible && ((c.missingForms?.length ?? 0) > 0
                      ? <span className="missing"> missing {c.missingForms!.join(', ')}</span>
                      : <span className="below"> below threshold</span>)}
                    {c.department && <span className="muted small block">{c.department}</span>}
                  </span>
                  <span className="right tabular">{c.tokens.toLocaleString()}</span>
                  {/* Clicking the cell padding must not toggle the entrant checkbox. */}
                  <span className="pick-cell" onClick={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }}>
                    <input type="checkbox" className="pick-box" checked={chosen === c.employeeId} onChange={() => toggleChosen(c.employeeId)}
                      aria-label={`Pick ${c.displayName} as the winner`} title="Pick as winner" />
                  </span>
                </label>
              ))}
              {visible.length === 0 && <p className="empty">No employees match.</p>}
            </div>
          </Card>
          <div className="sticky-actions">
            <div className="row-between">
              <strong className="tabular">{selected.size} selected</strong>
              <div className="gap">
                <button className="btn-link" onClick={selectAllVisible}>Select all</button>
                <button className="btn-link" onClick={clearAll}>Clear all</button>
              </div>
            </div>
            {chosenName
              ? <div className="pick-note">Winner picked: <strong>{chosenName}</strong>. The wheel will land on them. Only admins see this. <button className="btn-link" onClick={() => setChosen(null)}>Clear pick</button>{chosenWonLast && <span className="block small">They won the last drawing; your pick overrides the no back-to-back rule.</span>}</div>
              : <div className="muted small">Optional: check "Winner" on one person to pick the winner. Leave it blank for a random draw.</div>}
            {selected.size < 2 && <div className="warn small">Select at least 2 names to run a drawing.</div>}
            <button className="btn btn-primary btn-block" disabled={selected.size < 2} onClick={() => setStep(2)}>Next: Settings →</button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <Card>
            <label className="field">
              <span>Spin duration</span>
              <select className="input" value={settings.spinSeconds} onChange={(e) => setSettings({ ...settings, spinSeconds: Number(e.target.value) })}>
                {SPIN_OPTIONS.map((s) => <option key={s} value={s}>{s} seconds{s === 10 ? ' (default)' : ''}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Prize</span>
              <select className="input" value={presetFor(settings.prizeName, settings.prizeValue)?.key ?? '__custom'}
                onChange={(e) => {
                  const p = PRIZE_PRESETS.find((x) => x.key === e.target.value);
                  setSettings(p ? { ...settings, prizeName: p.name, prizeValue: String(p.value) } : { ...settings, prizeName: '', prizeValue: '' });
                }}>
                {PRIZE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                <option value="__custom">Custom prize…</option>
              </select>
            </label>
            <label className="field">
              <span>Prize name</span>
              <input className="input" maxLength={200} value={settings.prizeName} onChange={(e) => setSettings({ ...settings, prizeName: e.target.value })} placeholder="e.g. Yeti Cooler" />
              <span className="muted small">Shop TV shows: <strong>{prizeDisplayName(settings.prizeName)}</strong>{prizeDisplayName(settings.prizeName) !== settings.prizeName.trim() && settings.prizeName.trim() ? ' (dollar amounts are hidden on the TV)' : ''}</span>
            </label>
            <label className="field">
              <span>Prize value (USD) — admin only, never shown on the shop TV</span>
              <div className="money-input"><span>$</span>
                <input className="input tabular" inputMode="decimal" value={settings.prizeValue} onChange={(e) => setSettings({ ...settings, prizeValue: e.target.value.replace(/[^\d.]/g, '') })} placeholder="0.00" />
              </div>
            </label>
            <label className="field">
              <span>Winner screen hold</span>
              <select className="input" value={settings.celebrationSeconds} onChange={(e) => setSettings({ ...settings, celebrationSeconds: Number(e.target.value) })}>
                {[20, 25, 30, 45, 60].map((s) => <option key={s} value={s}>{s} seconds</option>)}
              </select>
            </label>
            <label className="field">
              <span>Scheduled time (optional — shows a countdown on the display)</span>
              <input className="input" type="datetime-local" value={settings.scheduledLocal} onChange={(e) => setSettings({ ...settings, scheduledLocal: e.target.value })} />
            </label>
            <label className="field">
              <span>Notes (optional)</span>
              <textarea className="input" rows={3} maxLength={1000} value={settings.notes} onChange={(e) => setSettings({ ...settings, notes: e.target.value })} placeholder="Weekly safety compliance drawing" />
            </label>
            {!settingsValid && <div className="warn small">Enter a prize name and a valid USD value (up to 2 decimals).</div>}
          </Card>
          <div className="sticky-actions two">
            <button className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-primary" disabled={!settingsValid || busy || !online} onClick={goPreview}>{busy ? 'Saving…' : 'Next: Preview →'}</button>
          </div>
        </>
      )}

      {step === 3 && drawing && (
        <>
          <Card>
            <div className="row-between">
              <div className="card-kicker">Drawing preview</div>
              <Pill kind={drawing.status === 'Staged' ? 'warn' : drawing.status === 'Cancelled' ? 'bad' : 'muted'}>{drawing.status}</Pill>
            </div>
            <ul className="summary">
              <li><strong className="tabular">{orderedEntrants.length}</strong> employees selected</li>
              <li>Prize: <strong>{drawing.prizeName}</strong>{drawing.prizeValue > 0 ? ` (${money(drawing.prizeValue)}, admin only)` : ''}</li>
              <li className="muted small">Shop TV shows: {prizeDisplayName(drawing.prizeName)} (no value)</li>
              <li>Winner: {drawing.chosenWinnerEmployeeId ? <><strong>{drawing.chosenWinnerName}</strong> (picked by admin; the wheel lands on them)</> : 'Random draw'}</li>
              <li>Spin duration: {drawing.spinSeconds} seconds</li>
              <li>Period: {drawing.periodLabel}</li>
              {(drawing.requiredForms?.length ?? 0) > 0 && <li>Required: {drawing.requiredForms!.join(', ')}</li>}
              {drawing.entries.some((e) => !e.metThreshold) && <li className="warn small">{drawing.entries.filter((e) => !e.metThreshold).length} selected {drawing.entries.filter((e) => !e.metThreshold).length === 1 ? 'person doesn\'t' : 'people don\'t'} qualify (added by hand)</li>}
              <li className="muted small">Ref {drawing.reference}</li>
            </ul>
            <div className="preview-wheel">
              <Wheel names={orderedEntrants} idle size={320} logoSrc="./brand/kes-logo-transparent.png" />
            </div>
            {drawing.status === 'Draft' && <p className="muted small">Sending to the display freezes the entrant list. {drawing.chosenWinnerEmployeeId ? 'The wheel will land on your picked winner when you press Start.' : 'The winner is chosen by the server when you press Start.'}</p>}
            {drawing.status === 'Staged' && <p className="muted small">Entrants are locked. The shop display is showing the ready screen.</p>}
          </Card>
          {drawing.status === 'Cancelled' ? (
            <button className="btn btn-secondary btn-block" onClick={() => navigate('/')}>Back to dashboard</button>
          ) : (
            <div className="sticky-actions two">
              {drawing.status === 'Draft'
                ? <button className="btn btn-secondary" onClick={() => setStep(2)}>← Back</button>
                : <button className="btn btn-danger-outline" onClick={() => setConfirm('cancel')}>Cancel</button>}
              {drawing.status === 'Draft'
                ? <button className="btn btn-primary" disabled={busy || !online} onClick={stage}>{busy ? 'Sending…' : 'Send to Display'}</button>
                : <button className="btn btn-go" disabled={busy || !online} onClick={() => setConfirm('start')}>Start Drawing</button>}
            </div>
          )}
          {!online && <div className="warn small center">Offline — reconnect to continue.</div>}
          {drawing.status === 'Draft' && <button className="btn-link center block" onClick={() => setConfirm('cancel')}>Cancel this drawing</button>}
        </>
      )}

      {confirm === 'start' && drawing && (
        <Modal title="Start the drawing?" onClose={() => setConfirm(null)} actions={<>
          <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Not yet</button>
          <button className="btn btn-go" disabled={busy} onClick={() => start(false)}>{busy ? 'Starting…' : 'Start now'}</button>
        </>}>
          <p>The server will pick the winner from <strong>{drawing.entries.length}</strong> entrants and the shop display will spin for {drawing.spinSeconds} seconds.</p>
          <p className="muted small">This cannot be undone or re-drawn.</p>
        </Modal>
      )}
      {confirm === 'offline' && (
        <Modal title="Shop display looks offline" onClose={() => setConfirm(null)} actions={<>
          <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Wait</button>
          <button className="btn btn-danger" disabled={busy} onClick={() => start(true)}>Start anyway</button>
        </>}>
          <p>The display has not checked in recently. If you start now, the winner is still chosen and saved, and the display will show the result when it reconnects.</p>
        </Modal>
      )}
      {confirm === 'cancel' && (
        <Modal title="Cancel this drawing?" onClose={() => setConfirm(null)} actions={<>
          <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Keep it</button>
          <button className="btn btn-danger" disabled={busy} onClick={cancel}>Cancel drawing</button>
        </>}>
          <p>No winner has been chosen yet. The cancellation is recorded in the audit log.</p>
        </Modal>
      )}
    </div>
  );
}
